export const RATING_ICONS = ['star', 'heart', 'circle'];
export const RATING_EMPTY_MODES = ['soft', 'outline', 'hidden'];
/**
 * Los iconos de una calificación, en SVG. Aquí y no en el editor: el panel
 * dibuja las mismas páginas en sus vistas previas, y tienen que coincidir.
 *
 * El «Rating» de Insertar eran cinco textos «★»: la tipografía decidía cómo se
 * veía la estrella, no había medias ni vacías, y la IA, al pedirle «textos más
 * largos», escribía dentro de cada estrella. Aquí son formas sólidas, dibujadas
 * igual en cualquier fuente, y no hay texto que reescribir.
 *
 * Formas propias y no las del catálogo de iconos: aquéllas son de contorno, y
 * una calificación se lee por el relleno —cuánto de cada icono está lleno—.
 */
const SHAPES = {
    star: '<path d="M11.1 2.6a1 1 0 0 1 1.8 0l2.5 5.1 5.6.8a1 1 0 0 1 .6 1.7l-4.1 4 1 5.6a1 1 0 0 1-1.5 1L12 18.2l-5 2.6a1 1 0 0 1-1.5-1l1-5.6-4.1-4a1 1 0 0 1 .6-1.7l5.6-.8z"/>',
    heart: '<path d="M12 20.7 4.6 13.5a5.2 5.2 0 0 1 0-7.4 5.2 5.2 0 0 1 7.4 0 5.2 5.2 0 0 1 7.4 0 5.2 5.2 0 0 1 0 7.4z"/>',
    circle: '<circle cx="12" cy="12" r="9"/>',
};
export const RATING_DEFAULTS = { value: 5, max: 5, size: 20, icon: 'star', empty: 'soft' };
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
/** Los valores ya saneados: los mismos para el dibujo y para la etiqueta accesible. */
export function normalizeRating(options) {
    const max = Math.round(clamp(Number.isFinite(options.max) ? options.max : RATING_DEFAULTS.max, 1, 10));
    // Décimas: 4.5 se dibuja como cuatro y media, 4.7 un poco más.
    const value = Math.round(clamp(Number.isFinite(options.value) ? options.value : RATING_DEFAULTS.value, 0, max) * 10) / 10;
    const size = Math.round(clamp(Number.isFinite(options.size) ? options.size : RATING_DEFAULTS.size, 8, 96));
    const icon = options.icon && options.icon in SHAPES ? options.icon : RATING_DEFAULTS.icon;
    const empty = options.empty ?? RATING_DEFAULTS.empty;
    return { value, max, size, icon, empty };
}
function shapeSvg(icon, size, variant) {
    const paint = variant === 'outline'
        ? 'fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"'
        : `fill="currentColor"${variant === 'soft' ? ' fill-opacity="0.22"' : ''}`;
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" ${paint} aria-hidden="true" style="display:block;flex:0 0 auto">${SHAPES[icon]}</svg>`;
}
/**
 * La fila de iconos, con la etiqueta que lee un lector de pantalla («4.5 de 5»)
 * ya en el idioma del sitio.
 *
 * La fila de iconos. Cada uno se llena según lo que le toca del valor: lleno,
 * vacío o, en el que cae la fracción, las dos capas con la llena recortada.
 */
export function ratingIconsHtml(options, ariaLabel) {
    const { value, max, size: px, icon, empty } = normalizeRating(options);
    const size = options.fluid ? '1em' : px;
    const box = options.fluid ? '1em' : `${px}px`;
    const emptyVariant = empty === 'outline' ? 'outline' : 'soft';
    const items = [];
    for (let index = 0; index < max; index++) {
        const fill = clamp(value - index, 0, 1);
        if (fill >= 1) {
            items.push(shapeSvg(icon, size, 'full'));
            continue;
        }
        // Ocultas: sólo lo que tiene algo de relleno.
        if (empty === 'hidden' && fill <= 0)
            break;
        if (fill <= 0) {
            items.push(shapeSvg(icon, size, emptyVariant));
            continue;
        }
        const base = empty === 'hidden' ? '' : shapeSvg(icon, size, emptyVariant);
        items.push(`<span style="position:relative;display:block;flex:0 0 auto;width:${box};height:${box}">${base}`
            + `<span style="position:absolute;top:0;left:0;bottom:0;width:${Math.round(fill * 100)}%;overflow:hidden">${shapeSvg(icon, size, 'full')}</span></span>`);
    }
    const score = options.showValue
        ? `<span aria-hidden="true" style="margin-left:6px;font-family:var(--wb-font-body,inherit);font-size:${options.fluid ? '1em' : `${Math.round(px * 0.8)}px`};font-weight:600;line-height:1;white-space:nowrap">${value}</span>`
        : '';
    return `<span role="img" aria-label="${ariaLabel(value, max).replace(/"/g, '&quot;')}" style="display:inline-flex;align-items:center;gap:inherit">${items.join('')}${score}</span>`;
}
//# sourceMappingURL=rating.js.map